import StyleIndex from "../../entities/GoogleSlidesStyleIndex";
import { PreviewSlide, SlideLine } from "../../pages/builder/editor/PreviewSlide";
import GoogleSlidesJsonGenerationService from "./GoogleSlidesJsonGenerationService";
import GoogleSlidesStyleIndexHelperMethods from "./GoogleSlidesStyleIndexHelperMethods";
import { SlideExportsHelper } from "../../utilities/SlideExportsHelper";
import RgbColor from "../../entities/RgbColor";

interface PersistentApplicationState {
	assetId: string;
	backgroundImagePath: string;
	fontColor: string;
}

export default class GoogleSlidesRequestBuilder {
	static createUpdateTextBackgroundRequests(textBoxId: string, endIndexes: StyleIndex[], startIndexes: StyleIndex[]) {
		let updateTextBackgroundReqs: any[] = [];
		let numIndexPairs = endIndexes.length;

		for (let x = 0; x < numIndexPairs; x++) {
			let currentEndIndex = endIndexes[x];
			let currentStartIndex = startIndexes[x];
			if (
				currentEndIndex.backgroundColor &&
				currentStartIndex.backgroundColor &&
				currentEndIndex.backgroundColor === currentStartIndex.backgroundColor
			) {
				let updateTextBackgroundReq = GoogleSlidesJsonGenerationService.createUpdateTextBackgroundRequest(
					textBoxId,
					currentEndIndex.index,
					currentStartIndex.index,
					currentEndIndex.backgroundColor
				);
				updateTextBackgroundReqs.push(updateTextBackgroundReq);
			} else {
				console.log("index backgroundColor style mismatch");
			}
		}

		return updateTextBackgroundReqs;
	}

	static createUpdateFontFamilyRequests(textBoxId: string, endIndexes: StyleIndex[], startIndexes: StyleIndex[]) {
		let fontFamilyReqs: any[] = [];
		let numIndexPairs = endIndexes.length;

		for (let x = 0; x < numIndexPairs; x++) {
			let currentEndIndex = endIndexes[x];
			let currentStartIndex = startIndexes[x];
			if (currentEndIndex.fontFamily && currentStartIndex.fontFamily && currentEndIndex.fontFamily === currentStartIndex.fontFamily) {
				let fontFamilyReq = GoogleSlidesJsonGenerationService.createUpdateFontFamilyRequest(
					textBoxId,
					currentEndIndex.index,
					currentStartIndex.index,
					currentEndIndex.fontFamily
				);
				fontFamilyReqs.push(fontFamilyReq);
			}
		}

		return fontFamilyReqs;
	}

	static createUpdateFontStyleRequests(textBoxId: string, endIndexes: number[], startIndexes: number[], type: "bold" | "italics" | "underlined") {
		let updateFontStyleReqs: any[] = [];
		let numIndexPairs = endIndexes.length;

		for (let x = 0; x < numIndexPairs; x++) {
			let currentEndIndex = endIndexes[x];
			let currentStartIndex = startIndexes[x];
			let updateFontStyleReq = null;

			if (type === "bold") {
				updateFontStyleReq = GoogleSlidesJsonGenerationService.makeFontBold(textBoxId, currentEndIndex, currentStartIndex);
			} else if (type === "italics") {
				updateFontStyleReq = GoogleSlidesJsonGenerationService.makeFontItalics(textBoxId, currentEndIndex, currentStartIndex);
			} else if (type === "underlined") {
				updateFontStyleReq = GoogleSlidesJsonGenerationService.makeFontUnderlined(textBoxId, currentEndIndex, currentStartIndex);
			}

			updateFontStyleReqs.push(updateFontStyleReq);
		}

		return updateFontStyleReqs;
	}

	static createBlockQuoteRequest(textBoxId: string, endIndex: number, startIndex: number, indentAmount: number) {
		let reqs: any[] = [];

		let makeQuoteTextItalics = GoogleSlidesJsonGenerationService.makeFontItalics(textBoxId, startIndex, endIndex);
		reqs.push(makeQuoteTextItalics);

		let makeQuoteTextIndented = GoogleSlidesJsonGenerationService.makeTextIndented(textBoxId, startIndex, endIndex, indentAmount);
		reqs.push(makeQuoteTextIndented);

		let makeFirstQuoteTextLineIndented = GoogleSlidesJsonGenerationService.makeTextFirstLineIndented(
			textBoxId,
			startIndex,
			endIndex,
			indentAmount
		);
		reqs.push(makeFirstQuoteTextLineIndented);

		return reqs;
	}

	static createUpdateFontSizeRequests(textBoxId: string, endIndexes: StyleIndex[], startIndexes: StyleIndex[]) {
		let fontSizeReqs: any[] = [];
		let numIndexPairs = endIndexes.length;

		for (let x = 0; x < numIndexPairs; x++) {
			let currentEndIndex = endIndexes[x];
			let currentStartIndex = startIndexes[x];
			if (currentEndIndex.fontSize && currentStartIndex.fontSize && currentEndIndex.fontSize === currentStartIndex.fontSize) {
				let fontSizeReq = GoogleSlidesJsonGenerationService.changeFontSize(
					textBoxId,
					currentEndIndex.index,
					currentStartIndex.index,
					currentEndIndex.fontSize
				);
				fontSizeReqs.push(fontSizeReq);
			} else {
				console.log("index fontSize style mismatch");
			}
		}

		return fontSizeReqs;
	}

	static getTextUpdateBoldRequests(boldIndexes: number[], textBoxId: string) {
		let boldEndIndexes = GoogleSlidesStyleIndexHelperMethods.getStyleEndIndexes(boldIndexes);
		let boldStartIndexes = GoogleSlidesStyleIndexHelperMethods.getStyleStartIndexes(boldIndexes);
		let makeFontBoldReqs = this.createUpdateFontStyleRequests(textBoxId, boldEndIndexes, boldStartIndexes, "bold");
		return makeFontBoldReqs;
	}

	static getTextUpdateItalicsRequests(italicsIndexes: number[], textBoxId: string) {
		let italicsEndIndexes = GoogleSlidesStyleIndexHelperMethods.getStyleEndIndexes(italicsIndexes);
		let italicsStartIndexes = GoogleSlidesStyleIndexHelperMethods.getStyleStartIndexes(italicsIndexes);
		let makeFontItalicsReqs = this.createUpdateFontStyleRequests(textBoxId, italicsEndIndexes, italicsStartIndexes, "italics");
		return makeFontItalicsReqs;
	}

	static getTextUpdateUnderlinedRequests(underlinedIndexes: number[], textBoxId: string) {
		let underlinedEndIndexes = GoogleSlidesStyleIndexHelperMethods.getStyleEndIndexes(underlinedIndexes);
		let underlinedStartIndexes = GoogleSlidesStyleIndexHelperMethods.getStyleStartIndexes(underlinedIndexes);
		let makeFontUnderlinedReqs = this.createUpdateFontStyleRequests(textBoxId, underlinedEndIndexes, underlinedStartIndexes, "underlined");
		return makeFontUnderlinedReqs;
	}

	static getTextUpdateFontFamilyRequests(fontFamilyIndexes: StyleIndex[], textBoxId: string) {
		let fontFamilyStartIndexes = GoogleSlidesStyleIndexHelperMethods.getFontFamilyStartIndexes(fontFamilyIndexes);
		let fontFamilyEndIndexes = GoogleSlidesStyleIndexHelperMethods.getFontFamilyEndIndexes(fontFamilyIndexes);
		let makeFontFamilyReqs = this.createUpdateFontFamilyRequests(textBoxId, fontFamilyEndIndexes, fontFamilyStartIndexes);
		return makeFontFamilyReqs;
	}

	static getTextUpdateFontSizeRequests(fontSizeIndexes: StyleIndex[], textBoxId: string) {
		let fontSizeStartIndexes = GoogleSlidesStyleIndexHelperMethods.getFontSizeStartIndexes(fontSizeIndexes);
		let fontSizeEndIndexes = GoogleSlidesStyleIndexHelperMethods.getFontSizeEndIndexes(fontSizeIndexes);
		let makeFontSizeReqs = this.createUpdateFontSizeRequests(textBoxId, fontSizeEndIndexes, fontSizeStartIndexes);
		return makeFontSizeReqs;
	}

	static getTextUpdateBackgroundColorRequests(backgroundColorIndexes: StyleIndex[], textBoxId: string) {
		let textBackgroundColorEndIndexes = GoogleSlidesStyleIndexHelperMethods.getTextBackgroundColorEndIndexes(backgroundColorIndexes);
		let textBackgroundColorStartIndexes = GoogleSlidesStyleIndexHelperMethods.getTextBackgroundColorStartIndexes(backgroundColorIndexes);
		let makeUpdateTextBackgroundColorReqs = this.createUpdateTextBackgroundRequests(
			textBoxId,
			textBackgroundColorEndIndexes,
			textBackgroundColorStartIndexes
		);
		return makeUpdateTextBackgroundColorReqs;
	}

	static getCreateListRequests(slide: PreviewSlide, textBoxId: string) {
		let reqs: any[] = [];

		slide.lines.forEach((line) => {
			let lastIndex = line.lineChars.length - 1;
			let lastChar = line.lineChars[lastIndex];
			let firstChar = line.lineChars[0];

			if (line.isOrderedList) {
				if (firstChar.indexInContent !== undefined && lastChar.indexInContent !== undefined) {
					let makeOrderedListRequest = GoogleSlidesJsonGenerationService.createListMarkerRequest(
						textBoxId,
						lastChar.indexInContent,
						firstChar.indexInContent,
						"ordered"
					);
					reqs.push(makeOrderedListRequest);
				}
			} else if (line.isUnorderedList) {
				if (firstChar.indexInContent !== undefined && lastChar.indexInContent !== undefined) {
					let makeUnorderedListRequest = GoogleSlidesJsonGenerationService.createListMarkerRequest(
						textBoxId,
						lastChar.indexInContent!,
						firstChar.indexInContent,
						"unordered"
					);
					reqs.push(makeUnorderedListRequest);
				}
			}
		});

		if (reqs.length > 0) {
			let indentBulletsRequest = GoogleSlidesJsonGenerationService.makeTextFirstLineIndented(textBoxId, undefined, undefined, 40);
			reqs.push(indentBulletsRequest);
		}

		return reqs;
	}

	static getCreateBlockQuoteRequests(slide: PreviewSlide, textBoxId: string) {
		let reqs: any[] = [];

		slide.lines.forEach((line, index) => {
			if (line.isBlockQuote) {
				let firstChar = line.lineChars[0];
				let lastIndex = line.lineChars.length - 1;
				let lastChar = line.lineChars[lastIndex];
				if (firstChar.indexInContent !== undefined && lastChar.indexInContent !== undefined) {
					let makeBlockQuoteRequest = this.createBlockQuoteRequest(
						textBoxId,
						lastChar.indexInContent + index,
						firstChar.indexInContent + index,
						40
					);
					reqs = reqs.concat(makeBlockQuoteRequest);
				}
			}
		});

		return reqs;
	}

	static makeParagraphIndented(textBoxId: string, startIndex: number | undefined, endIndex: number | undefined, magnitude: number) {
		let reqs: any = [];

		let indentParagraphText = GoogleSlidesJsonGenerationService.makeTextIndented(textBoxId, startIndex, endIndex, magnitude);
		reqs.push(indentParagraphText);

		let indentFirstLineText = GoogleSlidesJsonGenerationService.makeTextFirstLineIndented(textBoxId, startIndex, endIndex, magnitude);
		reqs.push(indentFirstLineText);

		return reqs;
	}

	static createTextRequestsWithNewLines(insertTextReqs: any[], line: SlideLine, textBoxId: string) {
		let lineText = line.lineChars.map((lineChar) => lineChar.char).join("");
		let insertTextReq = GoogleSlidesJsonGenerationService.insertText(lineText + "\n", textBoxId);
		insertTextReqs.unshift(insertTextReq);
	}

	static insertNewLineAndCorrectIndexes(lines: SlideLine[]) {
		let charCount = 0;
		lines.forEach((line) => {
			line.lineChars = line.lineChars.concat([
				{
					char: "\n",
					indexInContent: line.lineChars.length,
					isBold: false,
					isItalics: false,
					isUnderlined: false,
					fontFamily: "Arial",
				},
			]);

			line.lineChars.forEach((lineChar) => {
				lineChar.indexInContent = charCount;
				charCount++;
			});
		});

		return lines;
	}

	static getSlideContentLength(lines: SlideLine[]) {
		let result = 0;
		lines.forEach((line) => {
			result += line.lineChars.length;
		});
		return result - 1;
	}

	static generateUpdateRequestBody(slides: PreviewSlide[], persistentApplicationState: PersistentApplicationState, assetBaseUrl: string) {
		const max_slide_height = 405;
		const max_slide_width = 650;
		let backgroundImagePath = assetBaseUrl + persistentApplicationState!.backgroundImagePath;
		let requests: any[] = [];

		requests.push(GoogleSlidesJsonGenerationService.deleteTitlePage());

		slides.forEach((slide) => {
			let fontSize = 42;

			let slideId = SlideExportsHelper.getNewGuid();
			requests.push(GoogleSlidesJsonGenerationService.createSlide(slideId));

			let textBoxId = SlideExportsHelper.getNewGuid();
			requests.push(GoogleSlidesJsonGenerationService.createShape(textBoxId, slideId, "TEXT_BOX", max_slide_height, max_slide_width));

			let insertTextReqs: any[] = [];
			slide.lines.forEach((line) => this.createTextRequestsWithNewLines(insertTextReqs, line, textBoxId));
			requests = requests.concat(insertTextReqs);

			let numLines = slide.lines.length;
			let boldIndexes = [];
			let italicsIndexes = [];
			let underlinedIndexes = [];
			let fontFamilyIndexes = [];
			let fontSizeIndexes: StyleIndex[] = [];
			let backgroundColorIndexes = [];

			slide.lines = this.insertNewLineAndCorrectIndexes(slide.lines);

			//becuase of the way google slides inserts the text, we need to loop backwards over the lines and characters
			let currentCharIndex = this.getSlideContentLength(slide.lines);
			for (let i = numLines; i > 0; i--) {
				let currentLine = slide.lines[i - 1];
				let lineLength = currentLine.lineChars.length;

				for (let j = lineLength; j > 0; j--) {
					let currentChar = currentLine.lineChars[j - 1];

					if (currentChar.isBold || currentLine.blockTag === "h1" || currentLine.blockTag === "h2") {
						boldIndexes.push(currentCharIndex);
					}

					if (currentChar.isItalics) {
						italicsIndexes.push(currentCharIndex);
					}

					if (currentChar.isUnderlined) {
						underlinedIndexes.push(currentCharIndex);
					}

					if (currentChar.fontFamily !== undefined) {
						fontFamilyIndexes.push({ index: currentCharIndex, fontFamily: currentChar.fontFamily });
					}

					if (currentLine.blockTag === "h1") {
						fontSizeIndexes.push({ index: currentCharIndex, fontSize: 72 });
					} else if (currentLine.blockTag === "h2") {
						fontSizeIndexes.push({ index: currentCharIndex, fontSize: 58 });
					} else {
						fontSizeIndexes.push({ index: currentCharIndex, fontSize: 42 });
					}

					if (currentChar.backgroundColor !== undefined) {
						let backgroundColor = SlideExportsHelper.hexToRgb(currentChar.backgroundColor) as RgbColor;
						backgroundColorIndexes.push({ index: currentCharIndex, backgroundColor });
					}

					currentCharIndex--;
				}
			}

			requests.push(GoogleSlidesJsonGenerationService.centerTextVerticle(textBoxId));
			requests = requests.concat(this.makeParagraphIndented(textBoxId, undefined, undefined, 20));
			requests.push(GoogleSlidesJsonGenerationService.changeFontSize(textBoxId, undefined, undefined, fontSize!));
			requests.push(GoogleSlidesJsonGenerationService.insertImageAsStretchedBackground(slideId, backgroundImagePath));
			if (persistentApplicationState!.fontColor.toLowerCase() === "white") {
				requests.push(GoogleSlidesJsonGenerationService.changeFontColor(textBoxId, persistentApplicationState!.fontColor));
			}

			let makeFontFamilyReqs = this.getTextUpdateFontFamilyRequests(fontFamilyIndexes, textBoxId);
			requests = requests.concat(makeFontFamilyReqs);

			let makeFontBoldReqs = this.getTextUpdateBoldRequests(boldIndexes, textBoxId);
			requests = requests.concat(makeFontBoldReqs);

			let makeFontItalicsReqs = this.getTextUpdateItalicsRequests(italicsIndexes, textBoxId);
			requests = requests.concat(makeFontItalicsReqs);

			let makeFontUnderlinedReqs = this.getTextUpdateUnderlinedRequests(underlinedIndexes, textBoxId);
			requests = requests.concat(makeFontUnderlinedReqs);

			let makeFontSizeReqs = this.getTextUpdateFontSizeRequests(fontSizeIndexes, textBoxId);
			requests = requests.concat(makeFontSizeReqs);

			let makeUpdateTextBackgroundColorReqs = this.getTextUpdateBackgroundColorRequests(backgroundColorIndexes, textBoxId);
			requests = requests.concat(makeUpdateTextBackgroundColorReqs);

			let hasListStyle = slide.lines.filter((line) => line.isOrderedList || line.isUnorderedList).length > 0;
			if (hasListStyle) {
				let createListReqs = this.getCreateListRequests(slide, textBoxId);
				requests = requests.concat(createListReqs);
			}

			let hasBlockQuoteStyle = slide.lines.filter((line) => line.isBlockQuote).length > 0;
			if (hasBlockQuoteStyle) {
				let createBlockQuoteReqs = this.getCreateBlockQuoteRequests(slide, textBoxId);
				requests = requests.concat(createBlockQuoteReqs);
			}

			let alignmentStyle = slide.lines.filter((line) => line.textAlignment)[0];
			if (alignmentStyle) {
				let alignment = "START";
				if (alignmentStyle.textAlignment === "left") {
					alignment = "START";
				}
				if (alignmentStyle.textAlignment === "right") {
					alignment = "END";
				}
				if (alignmentStyle.textAlignment === "center") {
					alignment = "CENTER";
				}
				if (alignmentStyle.textAlignment === "justify") {
					alignment = "JUSTIFIED";
				}
				let createAlignmentReq = GoogleSlidesJsonGenerationService.makeTextAligned(textBoxId, alignment);
				requests.push(createAlignmentReq);
			}
		});

		return { requests };
	}
}
